import axios from 'axios';
import { toast } from 'react-toastify';
import baseURL from '../utils/baseURL';
import { isSessionMismatch, forceLogout } from '../utils/sessionGuard';

// Endpoints that don't require an auth token and must work even from an
// exiled tab — otherwise the user can't log back in after being kicked out
// by another tab's session change. /auth/refresh is intentionally NOT here:
// a stale tab refreshing tokens is exactly what the guard is preventing.
const PUBLIC_AUTH_PATHS = [
  '/auth/login',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/send-otp',
  '/auth/verify-otp',
  '/auth/emailExist',
  '/auth/mobileNoExist',
];

const isPublicAuthRequest = (url = '') =>
  PUBLIC_AUTH_PATHS.some((p) => url.includes(p));

// Global error handler reference (will be set by ErrorProvider)
let globalErrorHandler = null;

export const setGlobalErrorHandler = (handler) => {
  globalErrorHandler = handler;
};

// Deduplication cache for toasts
const toastCache = new Map();
const TOAST_DEDUPE_WINDOW = 5000;

/**
 * Custom hook to create an Axios instance with authentication interceptors
 * @returns {AxiosInstance} Configured Axios instance
 */
const useAxiosAuth = () => {
  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
    },
  });

  // Request interceptor to attach access token
  instance.interceptors.request.use(
    (config) => {
      // Pre-flight session check. If another tab in this browser has logged
      // in or out as a different user, this tab is stale — abort the request
      // before it goes out with the wrong token, and force a clean logout so
      // we don't display data from one user signed by another user's token.
      // Skip for public auth endpoints so an exiled tab can still log back in.
      if (!isPublicAuthRequest(config.url) && isSessionMismatch()) {
        forceLogout();
        return Promise.reject(new axios.Cancel('Session changed in another tab'));
      }

      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor for error handling and token refresh
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Helper function to show deduplicated toasts
      const showToast = (message, type = 'error') => {
        const cacheKey = `${type}-${message}`;
        const currentTime = Date.now();
        const lastShown = toastCache.get(cacheKey);

        if (!lastShown || currentTime - lastShown >= TOAST_DEDUPE_WINDOW) {
          toast[type](message, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          toastCache.set(cacheKey, currentTime);
        }
      };

      // Handle network errors - use global error handler instead of toast
      if (error.code === 'ERR_NETWORK') {
        if (globalErrorHandler) {
          globalErrorHandler.setNetworkError();
        }
        return Promise.reject(error);
      }

      // Handle server errors (503+) - use global error handler instead of toast
      if (error.response?.status >= 503) {
        if (globalErrorHandler) {
          globalErrorHandler.setServerError({ status: error.response.status });
        }
        return Promise.reject(error);
      }

      // Handle 401 errors and attempt token refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Backend signals an explicitly revoked token (logout). No point
        // refreshing — the refresh token was blacklisted at the same time.
        if (error.response?.data?.code === 'TOKEN_REVOKED') {
          forceLogout();
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            showToast('Session expired. Please log in again.');
            localStorage.clear();
            window.location.href = '/login';
            return Promise.reject(error);
          }

          // Call refresh token endpoint
          const response = await axios.post(`${baseURL}/auth/refresh`, { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data.data;

          // Store new tokens
          localStorage.setItem('accessToken', accessToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // Update the original request with the new access token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          showToast('Unable to refresh token. Please log in again.');
          console.error('Token refresh failed:', refreshError);
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      // Handle other errors
      const errorMessage = error.response?.data?.message?.en || 'An error occurred';
      // showToast(errorMessage);
      return Promise.reject(error);
    }
  );

  return instance;
};

export default useAxiosAuth;